import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

// A countdown for one device. The host's timer and each copresenter's timer are
// unrelated clocks that happen to look the same — nothing here is synced, and
// nothing here talks to the backend. "They manage their own time" is the
// requirement, and it's also why this is ~200 lines instead of a protocol.

export const DEFAULT_DURATION_SECONDS = 5 * 60;
const STEP_SECONDS = 60;
const MIN_DURATION_SECONDS = 60;
const MAX_DURATION_SECONDS = 180 * 60;

// Amber under one minute left, not at a percentage. The stepper moves in
// minutes, so the warning lands in the unit the presenter is already thinking
// in — and a percentage would put a 45-minute talk's warning nine minutes out,
// which isn't a warning.
//
// STRICTLY under: 01:00 is still the calm colour, 00:59 is the warning. Going
// amber ON the minute would mean a host who sets a 1-minute timer watches it
// turn the instant they press play, which reads as an error rather than as time
// running out.
const WARN_SECONDS = 60;

// 250ms, not 1000ms. A one-second interval that happens to start mid-second
// renders every boundary up to a second late; it doesn't break anything, it
// just looks broken. We only re-render when the formatted string changes, so
// the extra ticks cost four integer comparisons a second.
const TICK_MS = 250;

// Hold-to-repeat. At a minute a press, 5 → 45 minutes is forty taps.
const HOLD_DELAY_MS = 500;
const HOLD_FAST_MS = 120;

// Tapping the time opens these. People tap rather than hold, and the long
// durations (20–90 minutes, lessons and workshops) were forty-tap trips on the
// stepper: a preset makes any of them two taps, and − / + fine-tune from there.
export const PRESET_MINUTES = [5, 10, 15, 20, 30, 45, 60, 90];

// How long the stepper has to settle before we report an adjustment. Without
// this a single hold emits one event per repeat and the intermediate values
// drown the number the presenter actually chose.
const ADJUST_SETTLE_MS = 800;

const DURATION_KEY = "copresent.timer.duration";
const runKey = (magicId: string) => `copresent.timer.run.${magicId}`;

type RunState = {
  // Wall-clock ms at which the current run began; null when paused.
  startedAt: number | null;
  // Banked elapsed ms from previous runs.
  accumulatedMs: number;
};

const IDLE: RunState = { startedAt: null, accumulatedMs: 0 };

// mm:ss, or h:mm:ss past the hour. Negative renders with a leading minus: the
// number is labelled "remaining", and remaining going below zero is what has
// actually happened.
export const formatRemaining = (seconds: number): string => {
  const negative = seconds < 0;
  const total = Math.abs(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const body = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  return negative ? `-${body}` : body;
};

export const clampDuration = (seconds: number): number =>
  Math.min(MAX_DURATION_SECONDS, Math.max(MIN_DURATION_SECONDS, seconds));

const readStoredDuration = (): number => {
  if (typeof window === "undefined") return DEFAULT_DURATION_SECONDS;
  const raw = window.localStorage.getItem(DURATION_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) ? clampDuration(parsed) : DEFAULT_DURATION_SECONDS;
};

const readStoredRun = (magicId: string): RunState => {
  if (typeof window === "undefined") return IDLE;
  try {
    const raw = window.localStorage.getItem(runKey(magicId));
    if (!raw) return IDLE;
    const parsed = JSON.parse(raw) as Partial<RunState>;
    const accumulatedMs =
      typeof parsed.accumulatedMs === "number" && Number.isFinite(parsed.accumulatedMs)
        ? parsed.accumulatedMs
        : 0;
    const startedAt =
      typeof parsed.startedAt === "number" && Number.isFinite(parsed.startedAt)
        ? parsed.startedAt
        : null;
    return { startedAt, accumulatedMs };
  } catch {
    // Corrupt entry — start clean rather than throwing on mount. A lost timer
    // is recoverable in one tap; a controller that won't render isn't.
    return IDLE;
  }
};

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ResetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 4 3 9 8 9" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <rect x="7" y="5" width="3.5" height="14" rx="1" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="1" />
  </svg>
);

type Tone = "default" | "warn" | "over";

const toneColour = (tone: Tone) =>
  tone === "over"
    ? "var(--danger, #FE4657)"
    : tone === "warn"
    ? "var(--warning, #F29339)"
    : "var(--text-primary)";

const Cluster = styled.div<{ $tone: Tone; $fill: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 12px;
  border: 1px solid
    ${(p) => (p.$tone === "default" ? "var(--border-chip)" : toneColour(p.$tone))};
  color: ${(p) => toneColour(p.$tone)};

  /* Fill spreads the controls to the edges so a thumb reaches − and + without
     crossing the numeral. Up to a phone's width: on a tablet, which now gets
     the same touch layout, edge to edge put a screen's width between the
     buttons and the number they change. */
  ${(p) =>
    p.$fill &&
    `width: 100%; max-width: 400px; box-sizing: border-box; justify-content: space-between;`}
`;

const Value = styled.span<{ $muted: boolean }>`
  /* Tabular figures are not cosmetic here: proportional digits change width as
     they count and the whole cluster twitches once a second. */
  font-variant-numeric: tabular-nums;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.01em;
  min-width: 72px;
  text-align: center;
  padding: 0 2px;
  color: ${(p) => (p.$muted ? "var(--text-fade)" : "inherit")};
`;

const TimerButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid var(--border-chip);
  background: transparent;
  color: inherit;
  cursor: pointer;
  /* Stops a hold on -/+ selecting the surrounding text, and stops iOS firing
     the magnifier on a long press. */
  user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  transition: background-color 0.12s, color 0.12s, border-color 0.12s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:enabled {
    background: var(--bg-chip-hover);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// The time doubles as the button that opens the presets. No chrome of its own:
// it should still read as the clock, not as a control competing with − and +.
const ValueButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 6px 0;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;

  &:hover {
    background: var(--bg-chip-hover);
  }
`;

// In the flow under the clock rather than floating: the timer sits in headers
// that clip, and pushing the content down for the two taps it's open is fine.
const Stack = styled.div<{ $fill: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  ${(p) => p.$fill && `width: 100%; max-width: 400px;`}
`;

const Presets = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
`;

const Preset = styled.button`
  appearance: none;
  min-height: 40px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid var(--border-chip);
  background: transparent;
  color: var(--text-primary);
  font: inherit;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  touch-action: manipulation;

  &:hover {
    background: var(--bg-chip-hover);
  }

  &[aria-pressed="true"] {
    border-color: currentColor;
    font-weight: 600;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 22px;
  background: var(--border-chip);
  margin: 0 2px;
`;

export type TimerAnalytics = {
  duration_seconds: number;
  remaining_seconds: number;
  overtime: boolean;
};

type Props = {
  // Scopes the persisted run, so two decks in two tabs don't share a clock.
  magicId: string;
  // Full-width layout for the mobile row; inline for the desktop toolbar.
  fill?: boolean;
  // Reports the live value to the parent so the collapsed pill can render it.
  // Called on every visible change, not every tick.
  //
  // `dirty` is what lets the parent keep this mounted through an entitlement
  // drop: it means a run exists (started, or paused with time banked), so the
  // clock is one someone is currently pacing to.
  onTick?: (state: {
    remainingSeconds: number;
    running: boolean;
    dirty: boolean;
    tone: Tone;
  }) => void;
  // Every control is instrumented. The parent owns the capture so these events
  // ride captureUi's mode / magic_id / presenter_id like everything else.
  onEvent?: (event: string, props: Record<string, unknown>) => void;
};

const PresentationTimer = ({ magicId, fill = false, onTick, onEvent }: Props) => {
  const [durationSeconds, setDurationSeconds] = useState<number>(readStoredDuration);
  const [run, setRun] = useState<RunState>(() => readStoredRun(magicId));
  const [now, setNow] = useState<number>(() => Date.now());
  const [presetsOpen, setPresetsOpen] = useState(false);

  const running = run.startedAt !== null;
  const elapsedMs =
    run.accumulatedMs + (run.startedAt !== null ? now - run.startedAt : 0);
  const remainingSeconds = Math.ceil((durationSeconds * 1000 - elapsedMs) / 1000);
  const overtime = remainingSeconds < 0;
  const tone: Tone = overtime ? "over" : remainingSeconds < WARN_SECONDS ? "warn" : "default";
  const dirty = running || run.accumulatedMs > 0;

  // Recompute from the wall clock every tick; never accumulate. A counter doing
  // `n -= TICK_MS` loses every callback the browser skips, and a backgrounded
  // phone is throttled to roughly one callback a minute — it would come back
  // minutes wrong. This comes back right, and is the shorter code.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [running]);

  // A phone coming out of a pocket should be correct on the first paint, not up
  // to a tick later.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(DURATION_KEY, String(durationSeconds));
  }, [durationSeconds]);

  useEffect(() => {
    if (!dirty) {
      window.localStorage.removeItem(runKey(magicId));
      return;
    }
    window.localStorage.setItem(runKey(magicId), JSON.stringify(run));
  }, [run, dirty, magicId]);

  const analytics = useCallback(
    (): TimerAnalytics => ({
      duration_seconds: durationSeconds,
      remaining_seconds: remainingSeconds,
      overtime,
    }),
    [durationSeconds, remainingSeconds, overtime]
  );

  // Fires once per run at the zero crossing, not once per tick past it.
  const overtimeReported = useRef(false);
  useEffect(() => {
    if (!running) return;
    if (overtime && !overtimeReported.current) {
      overtimeReported.current = true;
      onEvent?.("timer_overtime", analytics());
    }
  }, [running, overtime, onEvent, analytics]);

  useEffect(() => {
    onTick?.({ remainingSeconds, running, dirty, tone });
  }, [onTick, remainingSeconds, running, dirty, tone]);

  // --- the stepper -------------------------------------------------------

  // Batches a hold into one settled report: from where, to where, how many
  // steps, and whether it was a tap or a hold.
  const pending = useRef<{ from: number; steps: number; held: boolean } | null>(null);
  const settleTimer = useRef<number | null>(null);

  const flushAdjust = useCallback(
    (to: number) => {
      const p = pending.current;
      if (!p) return;
      pending.current = null;
      onEvent?.("timer_adjusted", {
        direction: to >= p.from ? "up" : "down",
        from_seconds: p.from,
        to_seconds: to,
        steps: p.steps,
        via: p.held ? "hold" : "tap",
        while_running: running,
        duration_seconds: to,
        remaining_seconds: remainingSeconds,
        overtime,
      });
    },
    [onEvent, running, remainingSeconds, overtime]
  );

  // The stepper's own copy of the duration. A hold fires every 120ms, which is
  // faster than a render, so reading `durationSeconds` from the closure would
  // step from a stale value and lose repeats.
  //
  // The alternative — a functional setState whose updater does the stepping —
  // is worse than it looks: React may defer or (in StrictMode) double-invoke an
  // updater, and this one has side effects, so the settle timeout would be
  // scheduled at the wrong time or twice.
  const durationRef = useRef(durationSeconds);

  const adjust = useCallback(
    (deltaSeconds: number, held: boolean) => {
      const current = durationRef.current;
      const next = clampDuration(current + deltaSeconds);
      if (next === current) return;

      durationRef.current = next;
      setDurationSeconds(next);

      if (!pending.current) pending.current = { from: current, steps: 0, held };
      pending.current.steps += 1;
      pending.current.held = pending.current.held || held;

      if (settleTimer.current) window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => flushAdjust(next), ADJUST_SETTLE_MS);
    },
    [flushAdjust]
  );

  // Press-and-hold, wired on pointer events so it works the same under a mouse
  // and a thumb. onClick is deliberately not used: it would double-count the
  // press that started the hold.
  const holdTimers = useRef<number[]>([]);
  const clearHold = useCallback(() => {
    holdTimers.current.forEach(window.clearTimeout);
    holdTimers.current.forEach(window.clearInterval);
    holdTimers.current = [];
  }, []);

  const startHold = useCallback(
    (deltaSeconds: number) => {
      adjust(deltaSeconds, false);
      const delay = window.setTimeout(() => {
        const repeat = window.setInterval(() => adjust(deltaSeconds, true), HOLD_FAST_MS);
        holdTimers.current.push(repeat);
      }, HOLD_DELAY_MS);
      holdTimers.current.push(delay);
    },
    [adjust]
  );

  useEffect(() => clearHold, [clearHold]);

  // A preset is one deliberate choice, so it reports at once instead of
  // settling. A stepper adjustment still waiting to settle reports first, so
  // the two events stay in the order they happened.
  const choosePreset = (minutes: number) => {
    setPresetsOpen(false);
    const current = durationRef.current;
    const next = clampDuration(minutes * 60);
    if (next === current) return;

    if (settleTimer.current) {
      window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
      flushAdjust(current);
    }

    durationRef.current = next;
    setDurationSeconds(next);

    const nextRemaining = Math.ceil((next * 1000 - elapsedMs) / 1000);
    onEvent?.("timer_adjusted", {
      direction: next >= current ? "up" : "down",
      from_seconds: current,
      to_seconds: next,
      steps: 1,
      via: "preset",
      while_running: running,
      duration_seconds: next,
      remaining_seconds: nextRemaining,
      overtime: nextRemaining < 0,
    });
  };

  // --- transport ---------------------------------------------------------

  const toggleRun = () => {
    if (running) {
      const bankedMs = run.accumulatedMs + (Date.now() - (run.startedAt as number));
      setRun({ startedAt: null, accumulatedMs: bankedMs });
      onEvent?.("timer_paused", analytics());
      return;
    }
    // Resuming is starting. A fresh start is one with nothing banked.
    const resumed = run.accumulatedMs > 0;
    if (!resumed) overtimeReported.current = false;
    setNow(Date.now());
    setRun({ startedAt: Date.now(), accumulatedMs: run.accumulatedMs });
    onEvent?.("timer_started", { ...analytics(), resumed });
  };

  const reset = () => {
    onEvent?.("timer_reset", { ...analytics(), was_running: running });
    overtimeReported.current = false;
    setRun(IDLE);
    setNow(Date.now());
  };

  const presetsId = `timer-presets-${magicId}`;
  const atMin = durationSeconds <= MIN_DURATION_SECONDS;
  const atMax = durationSeconds >= MAX_DURATION_SECONDS;

  return (
    <Stack
      $fill={fill}
      onKeyDown={(e) => {
        if (e.key === "Escape") setPresetsOpen(false);
      }}
    >
      <Cluster $tone={tone} $fill={fill} data-testid="timer">
        <TimerButton
          type="button"
          aria-label="One minute less"
          title="One minute less"
          disabled={atMin}
          onPointerDown={() => startHold(-STEP_SECONDS)}
          onPointerUp={clearHold}
          onPointerLeave={clearHold}
          onPointerCancel={clearHold}
        >
          <MinusIcon />
        </TimerButton>

        {/* aria-live is off on purpose: a region that announces every second is
            unusable. Transitions are announced by the buttons' own labels and by
            the parent's polite region. */}
        <ValueButton
          type="button"
          aria-label={`${formatRemaining(remainingSeconds)} remaining. Choose a duration`}
          title="Choose a duration"
          aria-expanded={presetsOpen}
          aria-controls={presetsId}
          onClick={() => setPresetsOpen((open) => !open)}
        >
          <Value
            role="timer"
            aria-live="off"
            aria-label={`${formatRemaining(remainingSeconds)} remaining`}
            $muted={!running && !dirty}
          >
            {formatRemaining(remainingSeconds)}
          </Value>
        </ValueButton>

        <TimerButton
          type="button"
          aria-label="One minute more"
          title="One minute more"
          disabled={atMax}
          onPointerDown={() => startHold(STEP_SECONDS)}
          onPointerUp={clearHold}
          onPointerLeave={clearHold}
          onPointerCancel={clearHold}
        >
          <PlusIcon />
        </TimerButton>

        <Divider aria-hidden="true" />

        <TimerButton
          type="button"
          aria-label="Reset timer"
          title="Reset timer"
          disabled={!dirty}
          onClick={reset}
        >
          <ResetIcon />
        </TimerButton>

        <TimerButton
          type="button"
          aria-label={running ? "Pause timer" : dirty ? "Resume timer" : "Start timer"}
          title={running ? "Pause timer" : dirty ? "Resume timer" : "Start timer"}
          onClick={toggleRun}
        >
          {running ? <PauseIcon /> : <PlayIcon />}
        </TimerButton>
      </Cluster>

      {presetsOpen && (
        <Presets id={presetsId} role="group" aria-label="Timer duration">
          {PRESET_MINUTES.map((minutes) => (
            <Preset
              key={minutes}
              type="button"
              aria-label={`${minutes} minutes`}
              aria-pressed={durationSeconds === minutes * 60}
              onClick={() => choosePreset(minutes)}
            >
              {minutes} min
            </Preset>
          ))}
        </Presets>
      )}
    </Stack>
  );
};

export default PresentationTimer;
